import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UsersIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores/uiStore';
import { useCustomers } from '@/hooks/useCustomers';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { SearchInput } from '@/components/ui/SearchInput';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { SkeletonTable } from '@/components/ui/Skeleton';
import { Avatar } from '@/components/ui/Avatar';
import { formatCurrency, formatRelativeTime } from '@/utils/format';

const segmentVariant: Record<string, 'purple' | 'info' | 'success' | 'default'> = { vip: 'purple', repeat: 'info', new: 'success', inactive: 'default' };

export function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeSiteId = useUIStore((s) => s.activeSiteId);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, refetch } = useCustomers(activeSiteId, { page, perPage, search: search || undefined });

  const handleSearch = useCallback((val: string) => { setSearch(val); setPage(1); }, []);

  const showSyncNote = !isLoading && data && data.items.length > 0 && data.items.every((c) => c.ordersCount === 0);

  if (!activeSiteId) return <EmptyState icon={UsersIcon} title={t('dashboard.selectStore')} />;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('customers.title')}</h1>

      <SearchInput value={search} onChange={handleSearch} placeholder={`${t('common.search')}...`} className="w-full sm:w-64" />

      {showSyncNote && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
          <span>{t('customers.syncNote')}</span>
        </div>
      )}

      <Card padding="sm">
        {isLoading ? (
          <SkeletonTable rows={10} cols={6} />
        ) : isError ? (
          <ErrorState onRetry={() => refetch()} />
        ) : data && data.items.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-3"></th>
                    <th className="px-4 py-3">{t('customers.name')}</th>
                    <th className="px-4 py-3">{t('customers.email')}</th>
                    <th className="px-4 py-3">{t('customers.ordersCount')}</th>
                    <th className="px-4 py-3">{t('customers.totalSpent')}</th>
                    <th className="px-4 py-3">{t('customers.segment')}</th>
                    <th className="px-4 py-3">{t('orders.date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((cust) => (
                    <tr key={cust.id} className="border-b border-gray-50 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3"><Avatar name={`${cust.firstName} ${cust.lastName}`} src={cust.avatarUrl} size="sm" /></td>
                      <td className="cursor-pointer px-4 py-3 font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400" onClick={() => navigate(`/customers/${cust.id}`)}>
                        {cust.firstName} {cust.lastName}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{cust.email}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{cust.ordersCount}</td>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{formatCurrency(cust.totalSpent)}</td>
                      <td className="px-4 py-3">
                        <Badge label={t(`customers.segments.${cust.segment}`)} variant={segmentVariant[cust.segment] ?? 'default'} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatRelativeTime(cust.dateCreated)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 px-4">
              <Pagination page={data.page} perPage={data.perPage} total={data.total} totalPages={data.totalPages} onPageChange={setPage} onPerPageChange={(pp) => { setPerPage(pp); setPage(1); }} />
            </div>
          </>
        ) : (
          <EmptyState icon={UsersIcon} title={search ? t('common.noResults') : t('customers.noCustomers')} actionLabel={search ? t('common.retry') : undefined} onAction={search ? () => setSearch('') : undefined} />
        )}
      </Card>
    </div>
  );
}
